import React, { useState, useEffect } from 'react';
import { User } from '../../types';
import { UserAvatar } from './UserAvatar';
import { FollowButton } from './FollowButton';
import { useUserProfile } from '../../hooks/useUser';

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  onViewFullProfile: (userId: string) => void;
}

export const UserProfileModal: React.FC<UserProfileModalProps> = ({
  isOpen,
  onClose,
  userId,
  onViewFullProfile
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { getUserById } = useUserProfile();

  // Empêcher le défilement du body quand le modal est ouvert
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && userId) {
      loadUserData();
    }
  }, [isOpen, userId]);

  const loadUserData = async () => {
    setLoading(true);
    try {
      const userData = await getUserById(userId);
      setUser(userData);
    } catch (error) {
      console.error('Erreur lors du chargement du profil:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleViewFullProfile = () => {
    if (user && user.id) {
      onViewFullProfile(user.id);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-md w-full max-h-[90vh] overflow-y-auto">
        {loading ? (
          // Loading skeleton
          <div className="p-6 animate-pulse">
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-16 h-16 bg-gray-700 rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-700 rounded w-32 mb-2"></div>
                <div className="h-3 bg-gray-700 rounded w-24"></div>
              </div>
            </div>
            <div className="space-y-2 mb-4">
              <div className="h-3 bg-gray-700 rounded"></div>
              <div className="h-3 bg-gray-700 rounded w-3/4"></div>
            </div>
          </div>
        ) : user ? (
          <div className="p-6">
            {/* Header avec bouton fermer */}
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-lg font-semibold text-white">Profil Utilisateur</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Image de bannière (miniature) */}
            {user.bannerImage && (
              <div className="h-20 rounded-lg overflow-hidden mb-4">
                <img 
                  src={user.bannerImage} 
                  alt="Banner" 
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Informations utilisateur */}
            <div className="flex items-start space-x-4 mb-4">
              <UserAvatar user={user} size="lg" />
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <h4 className="text-xl font-bold text-white">
                    {user.displayName || user.username}
                  </h4>
                  {user.isVerified && (
                    <span className="text-green-500">✓</span>
                  )}
                </div>
                {user.username && user.displayName && (
                  <p className="text-gray-400 text-sm mb-2">@{user.username}</p>
                )}
                <div className="flex items-center space-x-4 text-sm text-gray-400">
                  <span>Niveau {user.level}</span>
                  <span>{user.xp} XP</span>
                </div>
              </div>
            </div>

            {/* Bio */}
            {user.bio && (
              <div className="mb-4">
                <p className="text-gray-300 text-sm leading-relaxed">
                  {user.bio}
                </p>
              </div>
            )}

            {/* Statistiques */}
            <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-700 rounded-lg">
              <div className="text-center">
                <p className="text-lg font-bold text-white">{user.followersCount || 0}</p>
                <p className="text-xs text-gray-400">Abonnés</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-white">{user.followingCount || 0}</p>
                <p className="text-xs text-gray-400">Abonnements</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-white">{user.badges?.length || 0}</p>
                <p className="text-xs text-gray-400">Badges</p>
              </div>
            </div>

            {/* Badges (aperçu limité) */}
            {user.badges && user.badges.length > 0 && (
              <div className="mb-6">
                <h5 className="text-white font-medium mb-3">🏆 Badges</h5>
                <div className="flex flex-wrap gap-2">
                  {user.badges.slice(0, 3).map((badge) => (
                    <div 
                      key={badge.id}
                      className={`p-2 rounded-lg border text-center text-xs ${
                        badge.rarity === 'legendary' ? 'border-yellow-500 bg-yellow-500/10' :
                        badge.rarity === 'epic' ? 'border-purple-500 bg-purple-500/10' :
                        badge.rarity === 'rare' ? 'border-blue-500 bg-blue-500/10' :
                        'border-gray-500 bg-gray-500/10'
                      }`}
                    >
                      <div className="text-lg mb-1">{badge.icon}</div>
                      <p className="text-white font-medium">{badge.name}</p>
                    </div>
                  ))}
                  {user.badges.length > 3 && (
                    <div className="p-2 rounded-lg border border-gray-500 bg-gray-500/10 text-center text-xs flex items-center justify-center">
                      <span className="text-gray-400">+{user.badges.length - 3}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Réseaux sociaux */}
            {user.socialLinks && Object.values(user.socialLinks).some(link => link) && (
              <div className="mb-6">
                <h5 className="text-white font-medium mb-3">🔗 Réseaux sociaux</h5>
                <div className="flex space-x-3">
                  {user.socialLinks.twitter && (
                    <a 
                      href={user.socialLinks.twitter} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 p-2 bg-blue-400/10 rounded-lg"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                      </svg>
                    </a>
                  )}
                  {user.socialLinks.telegram && (
                    <a 
                      href={user.socialLinks.telegram} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 p-2 bg-blue-400/10 rounded-lg"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                      </svg>
                    </a>
                  )}
                  {user.socialLinks.website && (
                    <a 
                      href={user.socialLinks.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 p-2 bg-blue-400/10 rounded-lg"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex space-x-3">
              <FollowButton 
                targetUserId={user.id} 
                size="md" 
                variant="primary"
              />
              <button
                onClick={handleViewFullProfile}
                className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
              >
                Voir le profil complet
              </button>
            </div>
          </div>
        ) : (
          // Erreur de chargement
          <div className="p-6 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 16.5C3.544 17.333 4.506 19 6.046 19z" />
              </svg>
              <p className="text-white mb-2">Erreur de chargement</p>
              <p className="text-sm">Impossible de charger le profil</p>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Fermer
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
